/**
 * <WallOfTenants /> — live activity ticker for the auth'd dashboard.
 *
 * v34.32 (Feb 2026): polls /api/wall/feed every 8s and renders the most
 * recent events as a vertically-scrolling feed with engine accent colors
 * + savings dollar amount per row. Header shows aggregate totals.
 *
 * Currently demo-mode (realistic simulated events). Backend contract is
 * stable so we can flip to real customer data without touching this widget.
 */
import React, { useEffect, useState } from 'react';
import { Users, TrendingUp, Activity, Loader2, ArrowRight, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 8000;

const TONE = {
  savings: { dot: 'bg-emerald-400', text: 'text-emerald-300' },
  policy:  { dot: 'bg-amber-400',   text: 'text-amber-300' },
  neutral: { dot: 'bg-sky-400',     text: 'text-sky-300' },
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return `${Math.round(diff / 3600)}h ago`;
};

const fmt$ = (n) => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

const WallOfTenants = ({ className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const fetchFeed = async () => {
      try {
        const res = await fetch(`${API}/api/wall/feed?limit=10`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.message || 'feed unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchFeed();
    timer = setInterval(fetchFeed, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  return (
    <div
      className={`rounded-xl border border-white/10 bg-slate-900 backdrop-blur overflow-hidden ${className}`}
      data-testid="wall-of-tenants-widget"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-slate-950">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-300/90">
            Wall of Tenants · Live
          </span>
          {data?.demo && (
            <span className="text-[9px] font-mono uppercase tracking-widest text-amber-300/80 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 ml-1">
              demo
            </span>
          )}
        </div>
        <Link
          to="/wall-of-tenants"
          className="text-[11px] text-slate-400 hover:text-emerald-300 inline-flex items-center gap-1 group"
          data-testid="wall-of-tenants-more"
        >
          See all <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>

      {/* Aggregate stats row */}
      <div className="grid grid-cols-2 gap-px bg-white/[0.04]">
        <div className="bg-slate-900 px-4 py-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-emerald-400" /> Saved · last 7 min
          </div>
          <div className="font-display font-black text-xl text-emerald-300 tabular-nums" data-testid="wall-total-saved">
            ${data ? fmt$(data.total_saved_today_usd) : '—'}
          </div>
        </div>
        <div className="bg-slate-900 px-4 py-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1">
            <Users className="w-3 h-3 text-sky-400" /> Active tenants
          </div>
          <div className="font-display font-black text-xl text-sky-300 tabular-nums" data-testid="wall-active-tenants">
            {data ? data.active_tenants : '—'}
          </div>
        </div>
      </div>

      {/* Event feed */}
      <div className="relative">
        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-500 text-xs gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Loading live feed…
          </div>
        )}
        {err && !data && (
          <div className="px-4 py-6 text-xs text-amber-300/80">
            Feed temporarily unavailable. Retrying every 8s…
          </div>
        )}
        {data && (
          <ul className="divide-y divide-white/[0.04] max-h-[420px] overflow-y-auto" data-testid="wall-event-list">
            {data.events.map((e) => {
              const tone = TONE[e.tone] || TONE.neutral;
              return (
                <li
                  key={e.id}
                  className="px-4 py-3 flex items-start gap-3 hover:bg-slate-950 transition-colors"
                  data-testid={`wall-event-${e.tenant}`}
                >
                  <span className={`shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="font-mono text-slate-300 truncate font-semibold">{e.tenant}</span>
                      <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500 font-mono">
                        <MapPin className="w-2.5 h-2.5" />{e.region}
                      </span>
                      <span className="text-[10px] text-slate-600 font-mono ml-auto">{relTime(e.at)}</span>
                    </div>
                    <div className="mt-0.5 text-[12.5px] text-slate-200 leading-snug">
                      <Activity className="w-3 h-3 inline mr-1 text-slate-500" />
                      {e.text}
                      {e.engine && (
                        <span className="ml-1.5 text-[10px] text-slate-500 font-mono">· {e.engine}</span>
                      )}
                    </div>
                    {e.saved_usd > 0 && (
                      <div className={`mt-1 text-[11px] font-mono font-semibold ${tone.text}`}>
                        +${fmt$(e.saved_usd)} saved
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WallOfTenants;
